import { useEffect, useMemo, useState } from "react";
import { InputAdornment } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { IZoneSchedule } from "../../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { commonLocalization } from "../../../../../../../functions/tableLocalization";
import { handleInitialCateringServiceSelect } from "./CateringMenuTable.partial";
import { ServiceCateringMenuRow } from "./typings/ServiceCateringMenu.types";
import { RemoteCateringMenu } from "../../../../../../ServiceManagement/Catering/Domain/Types/Remote-CateringMenu.types";
import {
  AssignedCateringMenu,
  CateringMenuIds
} from "../../../../../Domain/Types/FloorPlan/CateringMenuAssignment.type";
import { useRemoteAssignCateringServices } from "../../../../../../../hooks/Remote/Services/CateringMenu/useRemoteAssignCateringServices";
import {
  renderServiceBookingLeadTime,
  renderServiceImage,
  renderServiceSetupTime
} from "../ServicePartial/ServiceCommonRender.partial";
import * as _ from "lodash";
import { ServiceTableBottomToolbar } from "../ServicePartial/ServiceTableBottomToolbar.component";
import { validateAndSetErrors } from "../ServicePartial/ServiceTimeSetValidation.funtion";
import { ServiceValidationWithId } from "../ServicePartial/typings/ServiceValidationWithId.type";
import {
  handleChangeBookingLeadTime,
  handleChangeSetUpTime
} from "../ServicePartial/ServiceHandleTimeChange.function";
import { EnlargedImageDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { withSeeditMrtInitialState } from "../../../../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../../../../themes/use-seedit-mrt-table-options";

type Props = {
  zoneSchedule: IZoneSchedule;
  allCateringServices: RemoteCateringMenu[] | undefined;
  assignedCateringItems: AssignedCateringMenu[];
  onChangeConfService: (id: number, service: { cateringServices: CateringMenuIds[] }) => void;
  onClose: () => void;
};

/**
 * A tables that displays catering menu
 */
export default function ServiceCateringMenuTable({
  zoneSchedule,
  allCateringServices,
  assignedCateringItems,
  onChangeConfService,
  onClose
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);
  const [isMandatory, setIsMandatory] = useState(false);

  const [data, setData] = useState<ServiceCateringMenuRow[]>();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [validationErrors, setValidationErrors] = useState<ServiceValidationWithId>({});

  const { mutateAsync: assignCateringServices, isLoading: isAssignCateringServicesLoading } =
    useRemoteAssignCateringServices();

  const handleAssign = () => {
    const selectedRow = table
      .getSelectedRowModel()
      .rows.map(({ original: { id, bookingLeadTime, setUpTime } }) => ({
        cateringMenuId: id,
        bookingLeadTime: bookingLeadTime ?? null,
        setUpTime: setUpTime ?? null,
        selectingIsMandatory: isMandatory
      }));

    const finishAssign = () => {
      assignCateringServices({
        zoneScheduleId: zoneSchedule.id,
        bookingServices: selectedRow
      }).then(() => {
        onChangeConfService(zoneSchedule.id, { cateringServices: selectedRow });
        onClose();
      });
    };

    validateAndSetErrors(
      selectedRow.map(val => ({ id: val.cateringMenuId, ...val })),
      setValidationErrors,
      finishAssign
    );
  };

  const column = useMemo<MRT_ColumnDef<ServiceCateringMenuRow>[]>(
    () => [
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderServiceImage(100, cell.row.original.picturePath, setShowBiggerImage),
        size: 80
      },
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200,
        enableEditing: false
      },
      {
        accessorKey: "description",
        header: t("Description"),
        size: 250,
        enableEditing: false
      },
      {
        header: t("Booking Lead Time"),
        accessorKey: "bookingLeadTime",
        size: 150,
        enableEditing: true,
        muiTableBodyCellProps: {
          sx: { textAlign: "end" }
        },
        Header: renderServiceBookingLeadTime(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            type: "number",
            helperText: validationErrors[row.original.id]?.bookingLeadTime,
            inputProps: { step: 1, min: 0.0 },
            error: !!validationErrors[row.original.id]?.bookingLeadTime,
            onChange: val => {
              handleChangeBookingLeadTime({
                val,
                rowId: row.original.id,
                validationErrors,
                setValidationErrors
              });
              row.original.bookingLeadTime = Number(val.target.value);
            },
            sx: { width: "150px" },
            InputProps: {
              endAdornment: (
                <InputAdornment sx={{ pl: 2 }} position="start">
                  {row.original.bookingLeadTime && row.original.bookingLeadTime > 1
                    ? t("Hours")
                    : t("Hour")}
                </InputAdornment>
              )
            }
          };
        }
      },
      {
        header: t("Setup Time"),
        accessorKey: "setUpTime",
        Header: renderServiceSetupTime(),
        muiTableBodyCellProps: {
          sx: { textAlign: "end" }
        },
        size: 150,
        enableEditing: true,
        muiEditTextFieldProps: ({ row }) => {
          return {
            type: "number",
            error: !!validationErrors[row.original.id]?.setUpTime,
            helperText: validationErrors[row.original.id]?.setUpTime,
            onChange: val => {
              handleChangeSetUpTime({
                val,
                rowId: row.original.id,
                validationErrors,
                setValidationErrors
              });
              row.original.setUpTime = Number(val.target.value);
            },
            inputProps: { step: 5, min: 0 },
            sx: { width: "150px" },
            InputProps: {
              endAdornment: (
                <InputAdornment sx={{ pl: 2 }} position="start">
                  {"mins"}
                </InputAdornment>
              )
            }
          };
        }
      }
    ],
    [i18n.language, validationErrors]
  );

  useEffect(() => {
    if (!allCateringServices?.length) return;

    const arrData = _.uniqBy(
      [...assignedCateringItems, ...allCateringServices],
      cat => cat.id
    ) as AssignedCateringMenu[];

    const initialMandatory = arrData.some(item => item.selectingIsMandatory === true);
    const modified = arrData.map(el => ({
      ...el,
      bookingLeadTime: el.bookingLeadTime ?? 1.0, // hour unit
      setUpTime: el.setUpTime ?? 15, // minute unit
      selectingIsMandatory: el.selectingIsMandatory ?? false
    }));

    setData(modified);
    setIsMandatory(initialMandatory);

    if (assignedCateringItems.length)
      handleInitialCateringServiceSelect(assignedCateringItems, setRowSelection);
  }, [zoneSchedule, allCateringServices, assignedCateringItems]);
  const seeditMrtOptions = useSeeditMrtTableOptions<ServiceCateringMenuRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 5, pageIndex: 0 }
    }),
    state: { isSaving: isAssignCateringServicesLoading, rowSelection },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    enableHiding: true,
    enableRowSelection: true,
    enableEditing: true,
    editDisplayMode: "table",
    paginationDisplayMode: "pages",
    positionToolbarAlertBanner: "none",
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setRowSelection,
    renderBottomToolbar: ({ table }) => (
      <ServiceTableBottomToolbar
        table={table as any}
        isMandatory={isMandatory}
        isDisabled={!table.getSelectedRowModel().rows.length}
        setIsMandatory={setIsMandatory}
        handleAssign={handleAssign}
        onClose={onClose}
      />
    ),
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("_editable_list_noRecords", { item: t("_noCatering") })
    },
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15], variant: "outlined" },
    muiTableContainerProps: {
      sx: { maxHeight: "calc(80vh - 250px)" }
    }
  });

  return (
    <>
      <MaterialReactTable table={table} />

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
